import { apiFetch, ApiHttpError, ApiNetworkError } from './client';

/**
 * Two-step signed-URL upload for user-supplied photos.
 *
 * 1. POST /v1/media/upload-url with the file's content-type. Backend mints a
 *    short-TTL signed PUT URL and tells us the byte cap. We don't ever stream
 *    the file through our own API.
 * 2. PUT the raw file bytes to that URL. Response includes the `object_key`
 *    that's later submitted as `photo_media_key` in /v1/stalls/submissions
 *    or pinned to a review via /v1/reviews/{id}/photos.
 */

export interface SignURLResponse {
  upload_url: string;
  method: string;
  expires_at: number;
  content_type: string;
  max_bytes: number;
}

export interface UploadedMedia {
  object_key: string;
  content_type: string;
  size: number;
  public_url?: string;
}

export const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type AllowedPhotoType = (typeof ALLOWED_PHOTO_TYPES)[number];

export function isAllowedPhotoType(t: string): t is AllowedPhotoType {
  return (ALLOWED_PHOTO_TYPES as readonly string[]).includes(t);
}

export const mediaApi = {
  /** Step 1: ask the backend to sign an upload URL for this content type. */
  requestUploadUrl: (contentType: AllowedPhotoType, token: string) =>
    apiFetch<SignURLResponse>('/v1/media/upload-url', {
      method: 'POST',
      body: { content_type: contentType },
      token,
    }),

  /**
   * Step 2: PUT the file bytes to the signed URL and parse the {object_key}
   * the backend returns. Uses `fetch` directly because the URL is signed for
   * a specific method+content-type combination and we don't want apiFetch's
   * Authorization header attached to a GCS or signed-token endpoint.
   */
  uploadPhoto: async (
    file: File,
    signed: SignURLResponse,
    signal?: AbortSignal,
  ): Promise<UploadedMedia> => {
    if (file.size > signed.max_bytes) {
      throw new ApiHttpError(413, {
        code: 'photo_too_large',
        message_en: `Photo is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max ${(signed.max_bytes / 1024 / 1024).toFixed(0)}MB.`,
        message_th: `รูปใหญ่เกินไป (${(file.size / 1024 / 1024).toFixed(1)}MB) สูงสุด ${(signed.max_bytes / 1024 / 1024).toFixed(0)}MB`,
      });
    }
    let res: Response;
    try {
      res = await fetch(signed.upload_url, {
        method: signed.method,
        headers: { 'Content-Type': signed.content_type },
        body: file,
        signal,
        credentials: 'omit',
        redirect: 'error',
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw new ApiNetworkError(e);
    }
    if (!res.ok) {
      const text = await res.text();
      let errEnv: { code?: string; message_en?: string; message_th?: string } = {};
      try {
        const parsed = JSON.parse(text) as { error?: typeof errEnv };
        if (parsed?.error) errEnv = parsed.error;
      } catch {
        /* opaque GCS error body */
      }
      throw new ApiHttpError(res.status, errEnv);
    }
    return (await res.json()) as UploadedMedia;
  },
};
